/**
 * File: markdownFences.ts
 * Description: CommonMark-compatible helpers for the hand-rolled markdown
 *   renderers in Archie. They accept the same fenced code blocks a real
 *   markdown parser does (up to three leading spaces, three or more backticks
 *   or tildes, any casing of the info string), so a Mermaid diagram that
 *   renders in one screen renders in every screen.
 * Author: GitHub Copilot
 * Created: 2026-09-17
 */

/** Opening/closing fence of a code block. */
const FENCE_LINE_PATTERN = /^ {0,3}(`{3,}|~{3,})[ \t]*([^\s`]*)/;

/** Info string identifying a Mermaid diagram block. */
export const MERMAID_LANGUAGE = 'mermaid';

export interface FenceInfo {
  /** Literal fence marker; a closing fence must be at least as long. */
  marker: string;
  /** Lower-cased first word of the info string, e.g. "mermaid". */
  language: string;
}

/**
 * Normalise Windows/old-Mac line endings.
 *
 * @param content - Raw markdown.
 * @returns The same markdown using \n only.
 */
export const normalizeLineEndings = (content: string): string =>
  content.replace(/\r\n?/g, '\n');

/**
 * Parse a markdown line as a code fence.
 *
 * @param line - A single markdown line.
 * @returns Fence details, or null when the line is not a fence.
 */
export const parseFenceLine = (line: string): FenceInfo | null => {
  const match = FENCE_LINE_PATTERN.exec(line);
  if (!match) {
    return null;
  }
  return { marker: match[1], language: (match[2] ?? '').toLowerCase() };
};

/**
 * Whether a fence closes the currently open block. A shorter fence or a
 * different fence character is literal content, not a terminator.
 *
 * @param fence - The fence found on the current line.
 * @param openMarker - The marker that opened the current block.
 * @returns True when the open block ends on this line.
 */
export const closesFence = (fence: FenceInfo, openMarker: string): boolean =>
  fence.marker[0] === openMarker[0] &&
  fence.marker.length >= openMarker.length;

/**
 * Collect the fenced code blocks written in the given language. A fence the
 * generator never closed still yields its block, matching how CommonMark
 * closes open fences at the end of the document.
 *
 * @param content - Markdown to scan.
 * @param language - Info string to match, e.g. "mermaid".
 * @returns The body of every matching block, trimmed.
 */
export const extractFencedBlocks = (
  content: string,
  language: string,
): string[] => {
  const blocks: string[] = [];
  let buffer: string[] = [];
  let openMarker = '';
  let openLanguage = '';

  const flush = (): void => {
    if (openLanguage === language.toLowerCase()) {
      blocks.push(buffer.join('\n').trim());
    }
    buffer = [];
    openMarker = '';
    openLanguage = '';
  };

  for (const line of normalizeLineEndings(content).split('\n')) {
    const fence = parseFenceLine(line);
    if (openMarker) {
      if (fence && closesFence(fence, openMarker)) {
        flush();
      } else {
        buffer.push(line);
      }
    } else if (fence) {
      openMarker = fence.marker;
      openLanguage = fence.language;
    }
  }

  if (openMarker) {
    flush();
  }
  return blocks;
};

/**
 * Remove the fenced code blocks written in the given language, so the
 * remaining prose can be rendered separately.
 *
 * @param content - Markdown to scan.
 * @param language - Info string to remove, e.g. "mermaid".
 * @returns The markdown without those blocks, trimmed.
 */
export const stripFencedBlocks = (
  content: string,
  language: string,
): string => {
  const wanted = language.toLowerCase();
  const kept: string[] = [];
  let openMarker = '';
  let openLanguage = '';
  let buffer: string[] = [];

  const flush = (closingLine: string | null): void => {
    if (openLanguage !== wanted) {
      kept.push(...buffer);
      if (closingLine !== null) {
        kept.push(closingLine);
      }
    }
    buffer = [];
    openMarker = '';
    openLanguage = '';
  };

  for (const line of normalizeLineEndings(content).split('\n')) {
    const fence = parseFenceLine(line);
    if (openMarker) {
      if (fence && closesFence(fence, openMarker)) {
        flush(line);
      } else {
        buffer.push(line);
      }
    } else if (fence) {
      openMarker = fence.marker;
      openLanguage = fence.language;
      buffer.push(line);
    } else {
      kept.push(line);
    }
  }

  if (openMarker) {
    flush(null);
  }
  return kept.join('\n').trim();
};
